// ============================================================
// SOUND MANAGER — Web Audio API, no external files
// ============================================================

type SoundName = 'pick' | 'place' | 'discard' | 'meld' | 'turn' | 'win';

interface SoundConfig {
  frequency: number;
  type: OscillatorType;
  duration: number;
  gainPeak: number;
  detune?: number;
  secondFreq?: number;
  secondDelay?: number;
}

const SOUNDS: Record<SoundName, SoundConfig> = {
  pick: {
    frequency: 880,
    type: 'sine',
    duration: 0.09,
    gainPeak: 0.18,
  },
  place: {
    frequency: 660,
    type: 'sine',
    duration: 0.1,
    gainPeak: 0.15,
  },
  discard: {
    frequency: 440,
    type: 'triangle',
    duration: 0.14,
    gainPeak: 0.2,
    detune: -30,
  },
  meld: {
    frequency: 523,
    type: 'sine',
    duration: 0.28,
    gainPeak: 0.25,
    secondFreq: 659,
    secondDelay: 0.07,
  },
  turn: {
    frequency: 740,
    type: 'sine',
    duration: 0.18,
    gainPeak: 0.22,
  },
  win: {
    frequency: 523,
    type: 'sine',
    duration: 0.6,
    gainPeak: 0.3,
    secondFreq: 784,
    secondDelay: 0.1,
  },
};

class SoundManager {
  private ctx: AudioContext | null = null;
  public isMuted = false;

  init(): void {
    if (this.ctx) return;
    try {
      this.ctx = new (window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)();
    } catch {
      console.warn('[SoundManager] Web Audio API not available');
    }
  }

  play(name: SoundName): void {
    if (this.isMuted) return;
    if (!this.ctx) this.init();
    if (!this.ctx) return;

    const cfg = SOUNDS[name];
    const ctx = this.ctx;
    const now = ctx.currentTime;

    try {
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }

      const playTone = (freq: number, startAt: number, dur: number, peak: number, det?: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = cfg.type;
        osc.frequency.setValueAtTime(freq, startAt);
        if (det) osc.detune.setValueAtTime(det, startAt);
        gain.gain.setValueAtTime(0, startAt);
        gain.gain.linearRampToValueAtTime(peak, startAt + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, startAt + dur);
        osc.start(startAt);
        osc.stop(startAt + dur + 0.02);
      };

      playTone(cfg.frequency, now, cfg.duration, cfg.gainPeak, cfg.detune);

      if (cfg.secondFreq && cfg.secondDelay !== undefined) {
        playTone(cfg.secondFreq, now + cfg.secondDelay, cfg.duration, cfg.gainPeak * 0.8);
      }
    } catch {
      // Silently ignore audio errors
    }
  }

  toggle(): void {
    this.isMuted = !this.isMuted;
  }
}

export const soundManager = new SoundManager();
export default soundManager;
